import { api, throwApiError } from "../../api/api";

export type FreelancerAfiliacao = "ECO" | "ZE";

export type FreelancerPublic = {
  id: string;
  nomeCompleto: string;
  cpf: string;
  contato?: string | null;
  /** Só vem nas listagens administrativas (`GET /freelancers`). */
  afiliacao?: FreelancerAfiliacao | null;
  /**
   * Preenchido quando o freelancer está desativado (soft delete). Só vem nas
   * listagens administrativas — é o que permite a coluna "Situação" funcionar
   * com o filtro "Todos".
   */
  deletedAt?: string | null;
  pix?: string | null;
  banco?: string | null;
  rua?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  storeIds: string[];
  createdAt: string;
  updatedAt: string;
};

export type CreateFreelancerRequest = {
  nomeCompleto: string;
  cpf: string;
  contato?: string;
  pix?: string;
  banco?: string;
  rua?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  storeIds?: string[];
};

export type UpdateFreelancerRequest = {
  nomeCompleto?: string;
  cpf?: string;
  contato?: string;
  pix?: string;
  banco?: string;
  rua?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  storeIds?: string[];
};

export type FreelancerLoginRequest = {
  cpf: string;
};

export type FreelancerLoginResponse = {
  accessToken: string;
  freelancer: {
    nomeCompleto: string;
    cpf: string;
  };
};

export type FreelancerStore = {
  id: string;
  nomeFantasia: string;
  endereco: string | null;
  estado?: string | null;
};

export type FreelancerStoresResponse = {
  items: FreelancerStore[];
};

export type PaginatedFreelancersResponse = {
  items: FreelancerPublic[];
  page: number;
  limit: number;
  total: number;
};

export type GetFreelancersPaginatedParams = {
  page?: number;
  limit?: number;
  nome?: string;
  status?: "active" | "deleted" | "all";
};

export type AssignMeToStoreRequest = {
  storeId: string;
};

export type AssignMeToStoreResponse = {
  id: string;
  stores: { storeId: string }[];
  updatedAt?: string;
};

export type UnassignMeFromStoreResponse = {
  id: string;
  stores: { storeId: string }[];
  updatedAt?: string;
};

export type GetMyAvailableStoresParams = {
  from?: string;
};

export type GetFreelancersByStoreParams = {
  page?: number;
  limit?: number;
};
export type GetMyScheduledStoresParams = {
  from?: string;
  to?: string;
};

export type MyScheduledStore = {
  id: string;
  nomeFantasia: string;
  endereco: string | null;
  nextShiftAt: string;
  days: string[];
};

export type MyScheduledStoresResponse = {
  items: MyScheduledStore[];
};

export type SlotFreelancerItem = {
  freelancerId: string;
  nomeCompleto: string;
  cpf: string;
  status: string;
  registeredAt: string;
};

export type PaginatedSlotFreelancersResponse = {
  items: SlotFreelancerItem[];
  page: number;
  limit: number;
  total: number;
};

export type GetFreelancersBySlotParams = {
  page?: number;
  limit?: number;
};

export type GetMyFutureSlotsParams = {
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
};

export type MyFutureSlotItem = {
  registrationId: string;
  status: string;
  slotId: string;
  day: string;
  startAt: string;
  store: {
    id: string;
    nomeFantasia: string;
    endereco: string | null;
  };
};

export type PaginatedMyFutureSlotsResponse = {
  items: MyFutureSlotItem[];
  page: number;
  limit: number;
  total: number;
};

export type PaginatedFreelancersByNameResponse = {
  items: FreelancerPublic[];
  page: number;
  limit: number;
  totalItens: number;
};

export type SearchFreelancersByStoreParams = {
  nome: string;
  page?: number;
  limit?: number;
};

export const createFreelancerApi = async (
  dto: CreateFreelancerRequest
): Promise<FreelancerPublic> => {
  try {
    const { data } = await api.post<FreelancerPublic>("/freelancers", dto);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao criar freelancer.");
    throw error;
  }
};

export const updateFreelancerByIdApi = async (
  token: string,
  id: string,
  dto: UpdateFreelancerRequest
): Promise<FreelancerPublic> => {
  try {
    const { data } = await api.patch<FreelancerPublic>(`/freelancers/${id}`, dto, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar freelancer.");
    throw error;
  }
};

export const updateFreelancerMeApi = async (
  token: string,
  dto: UpdateFreelancerRequest
): Promise<FreelancerPublic> => {
  try {
    const { data } = await api.patch<FreelancerPublic>(`/freelancers/me`, dto, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar freelancer.");
    throw error;
  }
};

export const loginFreelancerApi = async (
  dto: FreelancerLoginRequest
): Promise<FreelancerLoginResponse> => {
  try {
    const { data } = await api.post<FreelancerLoginResponse>(
      "/freelancers/login",
      dto
    );
    return data;
  } catch (error) {
    throwApiError(error, "CPF inválido.");
    throw error;
  }
};

export const getMyFreelancerStoresApi = async (
  token: string
): Promise<FreelancerStoresResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<FreelancerStoresResponse>(
      "/freelancers/me/stores",
      {
        headers: { Authorization: `Bearer ${rawToken}` },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar lojas do freelancer.");
    throw error;
  }
};

export const getFreelancersPaginatedApi = async (
  token: string,
  params: GetFreelancersPaginatedParams = {}
): Promise<PaginatedFreelancersResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<PaginatedFreelancersResponse>("/freelancers", {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 10,
        ...(params.nome?.trim() ? { nome: params.nome.trim() } : {}),
        ...(params.status ? { status: params.status } : {}),
      },
      headers: { Authorization: `Bearer ${rawToken}` },
    });

    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar freelancers.");
    throw error;
  }
};

export const assignMeToStoreApi = async (
  token: string,
  dto: AssignMeToStoreRequest
): Promise<AssignMeToStoreResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.post<AssignMeToStoreResponse>(
      "/freelancers/assign-me/stores",
      dto,
      {
        headers: { Authorization: `Bearer ${rawToken}` },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao vincular loja ao freelancer.");
    throw error;
  }
};

export const unassignMeFromStoreApi = async (
  token: string,
  storeId: string
): Promise<UnassignMeFromStoreResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.delete<UnassignMeFromStoreResponse>(
      `/freelancers/assign-me/stores/${storeId}`,
      {
        headers: { Authorization: `Bearer ${rawToken}` },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao desvincular loja do freelancer.");
    throw error;
  }
};

export const getMyAvailableStoresApi = async (
  token: string,
  params: GetMyAvailableStoresParams = {}
): Promise<FreelancerStoresResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<FreelancerStoresResponse>(
      "/freelancers/me/stores/available",
      {
        params: {
          ...(params.from ? { from: params.from } : {}),
        },
        headers: { Authorization: `Bearer ${rawToken}` },
      }
    );

    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar lojas com horários disponíveis.");
    throw error;
  }
};

export const getFreelancersByStoreApi = async (
  token: string,
  storeId: string,
  params: GetFreelancersByStoreParams = {}
): Promise<PaginatedFreelancersResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<PaginatedFreelancersResponse>(
      `/freelancers/stores/${storeId}`,
      {
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
        },
        headers: { Authorization: `Bearer ${rawToken}` },
      }
    );

    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar freelancers da loja.");
    throw error;
  }
};

export const getMyScheduledStoresApi = async (
  token: string,
  params: GetMyScheduledStoresParams = {}
): Promise<MyScheduledStoresResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<MyScheduledStoresResponse>(
      "/freelancers/me/scheduled-stores",
      {
        params: {
          ...(params.from ? { from: params.from } : {}),
          ...(params.to ? { to: params.to } : {}),
        },
        headers: { Authorization: `Bearer ${rawToken}` },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar lojas com horários futuros.");
    throw error;
  }
};

export const getFreelancersBySlotApi = async (
  token: string,
  slotId: string,
  params: GetFreelancersBySlotParams = {}
): Promise<PaginatedSlotFreelancersResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<PaginatedSlotFreelancersResponse>(
      `/freelancers/slots/${slotId}/freelancers`,
      {
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
        },
        headers: { Authorization: `Bearer ${rawToken}` },
      }
    );

    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar freelancers do horário.");
    throw error;
  }
};

export const getMyFutureSlotsApi = async (
  token: string,
  params: GetMyFutureSlotsParams = {}
): Promise<PaginatedMyFutureSlotsResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<PaginatedMyFutureSlotsResponse>(
      "/freelancers/me/slots",
      {
        params: {
          ...(params.from ? { from: params.from } : {}),
          ...(params.to ? { to: params.to } : {}),
          ...(params.page ? { page: params.page } : {}),
          ...(params.limit ? { limit: params.limit } : {}),
        },
        headers: { Authorization: `Bearer ${rawToken}` },
      }
    );

    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar horários futuros do freelancer.");
    throw error;
  }
};

export const searchFreelancersByStoreApi = async (
  token: string,
  storeId: string,
  params: SearchFreelancersByStoreParams
): Promise<PaginatedFreelancersByNameResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<PaginatedFreelancersByNameResponse>(
      `/freelancers/stores/${storeId}/search`,
      {
        params: {
          nome: params.nome,
          page: params.page ?? 1,
          limit: params.limit ?? 10,
        },
        headers: { Authorization: `Bearer ${rawToken}` },
      }
    );

    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar freelancers da loja por nome.");
    throw error;
  }
};

export const getFreelancerMeApi = async (token: string): Promise<FreelancerPublic> => {
  try {
    const { data } = await api.get<FreelancerPublic>("/freelancers/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar seus dados.");
    throw error;
  }
};

export const deactivateFreelancerByIdApi = async (
  token: string,
  id: string
): Promise<FreelancerPublic> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.patch<FreelancerPublic>(`/freelancers/${id}/deactivate`, {}, {
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao desativar freelancer.");
    throw error;
  }
};

export const reactivateFreelancerByIdApi = async (
  token: string,
  id: string
): Promise<FreelancerPublic> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.patch<FreelancerPublic>(`/freelancers/${id}/reactivate`, {}, {
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao reativar freelancer.");
    throw error;
  }
};
